import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FlexSectionHeader } from '../../style'
import { SyncOutlined } from '@ant-design/icons'

import { Avatar, Button, Space, Table, Typography, Tag } from 'antd'
import { useParams } from 'react-router'
import useCoursePrivilege from '../../../hooks/useCourseprivilege'
import { STUDENT } from '../../../constants/userRoles'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import PlagarismTag from '../../../components/PlagarismTag'
import {
  autoGrade,
  checkPlagiarism,
  getAllSubmissions
} from '../../../reducers/submissionsReducer'
import Spinner from '../../../components/Spinner'

import AutoGradingTag from '../../../components/AutoGradingTag'

const { Title, Text } = Typography
const { Column } = Table

const Submissions = (props) => {
  const dispatch = useDispatch()

  const { courseId, assessmentId } = useParams()
  const { privilege } = useCoursePrivilege()

  const data = useSelector((state) => state.submissions.data)
  const loading = useSelector((state) => state.submissions.loading)
  const [isAutoGrading, setIsAutoGrading] = useState(false)
  const [isCheckingPlagiarism, setIsCheckingPlagiarism] = useState(false)

  const { assessment, submissions } = data || {}

  useEffect(() => {
    dispatch(getAllSubmissions(courseId, assessmentId))
  }, [courseId, assessmentId])

  useEffect(() => {
    if (!submissions || submissions.length === 0) return

    const hasProcessing = submissions.some(
      (sub) => sub.autoGradingStatus === 'processing'
    )

    if (!hasProcessing && isAutoGrading) {
      setIsAutoGrading(false)
      return
    }

    if (hasProcessing) {
      const interval = setInterval(() => {
        dispatch(getAllSubmissions(courseId, assessmentId))
      }, 2000)

      return () => clearInterval(interval)
    }
  }, [submissions, courseId, assessmentId, dispatch, isAutoGrading])

  const handleAutoGrade = async () => {
    setIsAutoGrading(true)
    await dispatch(autoGrade(courseId, assessmentId))
  }

  useEffect(() => {
    if (!submissions || submissions.length === 0) return

    const hasUncalculated = submissions?.some(
      (sub) =>
        sub.plagarismStatus === 'processing' ||
        sub.plagarismStatus === 'unCalculated'
    )

    if (!hasUncalculated && isCheckingPlagiarism) {
      setIsCheckingPlagiarism(false)
      return
    }

    if (hasUncalculated && isCheckingPlagiarism) {
      const interval = setInterval(() => {
        dispatch(getAllSubmissions(courseId, assessmentId))
      }, 3000)

      return () => clearInterval(interval)
    } else {
      setIsCheckingPlagiarism(false)
    }
  }, [submissions, courseId, assessmentId, dispatch, isCheckingPlagiarism])

  const handleCheckPlagiarism = async () => {
    setIsCheckingPlagiarism(true)
    await dispatch(checkPlagiarism(courseId, assessmentId))
  }

  if (loading || !assessment) return <Spinner size="large" />

  return (
    <>
      <FlexSectionHeader>
        <Title level={3}>
          All Submissions for{' '}
          <span style={{ color: '#1677ff' }}>{assessment?.title}</span>
        </Title>
        <Space>
          {privilege !== STUDENT && assessment?.questionsType === 'online' && (
            <Button onClick={handleAutoGrade} type="primary">
              {isAutoGrading ? 'Auto Grading...' : 'Auto Grade'}
            </Button>
          )}
          {privilege !== STUDENT &&
            assessment?.submissionType === 'written' && (
              <Button onClick={handleCheckPlagiarism} type="primary">
                {isCheckingPlagiarism
                  ? 'Check Plagiarisming...'
                  : 'Check Plagiarism'}
              </Button>
            )}
        </Space>
      </FlexSectionHeader>

      {submissions?.some((sub) => sub.autoGradingStatus === 'processing') && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Tag icon={<SyncOutlined spin />} color="processing">
            Auto grading in progress...
          </Tag>
        </div>
      )}

      {submissions?.some((sub) => sub.plagarismStatus === 'processing') && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Tag icon={<SyncOutlined spin />} color="processing">
            Plagiarism check in progress...
          </Tag>
        </div>
      )}

      <Table
        pagination={false}
        style={{ marginTop: '24px' }}
        rowKey={(submission) => submission.id}
        dataSource={submissions}
      >
        <Column
          title="Photo"
          dataIndex={['student', 'photo']}
          render={(img) => <Avatar src={img} />}
        />
        <Column title="Name" dataIndex={['student', 'name']} />
        <Column
          title="Submission Time"
          dataIndex="submittedAt"
          render={(time) => {
            if (!time) return '-'
            else
              return DateTime.fromISO(time).toLocaleString(
                DateTime.DATETIME_MED
              )
          }}
        />
        {assessment?.type === 'Assignment' && (
          <Column
            title="Status"
            dataIndex="status"
            render={(text, record) => {
              if (!record.submittedAt) return '-'

              const submissionTime = DateTime.fromISO(record.submittedAt)
              const dueDate = DateTime.fromISO(record.assessment.dueDate)

              if (submissionTime <= dueDate) {
                return <Tag color="green">On Time</Tag>
              } else {
                return <Tag color="red">Late</Tag>
              }
            }}
          />
        )}
        {assessment?.submissionType === 'written' && (
          <Column
            title="Plagirism Degree"
            dataIndex="plagarismStatus"
            render={(plagarismStatus) => (
              <PlagarismTag status={plagarismStatus} />
            )}
          />
        )}
        {assessment?.questionsType === 'online' && (
          <Column
            title="Auto Grading"
            dataIndex="autoGradingStatus"
            render={(autoGradingStatus) => (
              <AutoGradingTag status={autoGradingStatus} />
            )}
          />
        )}
        <Column
          title={`Score / ${assessment.maxScore}`}
          dataIndex="score"
          render={(score) => (score === undefined ? '-' : score)}
        />
        <Column
          title="Action"
          render={(text, record, index) => (
            <Link
              to={{
                pathname: `/app/course/${courseId}/assessment/${assessmentId}/grade`,
                state: { index: index }
              }}
            >
              Grade
            </Link>
          )}
        />
      </Table>
    </>
  )
}

export default Submissions
